import { PRODUCT_LINES, PRODUCT_LINE_TO_DEVICES, ProductLineKey } from './products';

export interface FilterOption {
  value: string;
  label: string;
  hint?: string;
}

export type FilterGroupId = 'authority' | 'severity' | 'product' | 'time' | 'status';

export interface FilterDefinition {
  key: string;
  label: string;
  helperText?: string;
  group: FilterGroupId;
  type: 'select' | 'cascade' | 'chips';
  options: FilterOption[] | ((ctx: FilterContext) => FilterOption[]);
  placeholder: string;
  clearValue: string;
  dependsOn?: string;
  chipLabel: string;
  chipClass: string;
  /** Display order in the toolbar row. Lower = further left. */
  order: number;
}

export interface FilterContext {
  values: Record<string, string>;
  counts?: Partial<Record<string, number>>;
}

export interface FilterGroupDefinition {
  id: FilterGroupId;
  label: string;
  description: string;
}

export const FILTER_GROUPS: readonly FilterGroupDefinition[] = [
  {
    id: 'authority',
    label: 'Regulatory Authority',
    description: 'Which regulatory body issued the signal.',
  },
  {
    id: 'severity',
    label: 'Priority & Triage',
    description: 'Urgency tier and current triage state.',
  },
  {
    id: 'product',
    label: 'Product Portfolio',
    description: 'Spacelabs product line and specific device model.',
  },
  {
    id: 'time',
    label: 'Time Range',
    description: 'Lookback window over ingested or published signals.',
  },
] as const;

const PRODUCT_LINE_OPTIONS: FilterOption[] = PRODUCT_LINES.map((line) => ({
  value: line.key,
  label: line.label,
  hint: `${PRODUCT_LINE_TO_DEVICES[line.key as ProductLineKey].length} devices`,
}));

const DEVICE_OPTIONS_FOR_LINE = (lineKey: string): FilterOption[] => {
  if (!lineKey || !PRODUCT_LINE_TO_DEVICES[lineKey as ProductLineKey]) {
    return [];
  }
  return PRODUCT_LINE_TO_DEVICES[lineKey as ProductLineKey].map((device) => ({
    value: device,
    label: device,
  }));
};

const ALL_DEVICE_OPTIONS: FilterOption[] = PRODUCT_LINES.flatMap((line) =>
  PRODUCT_LINE_TO_DEVICES[line.key as ProductLineKey].map((device) => ({
    value: device,
    label: `${device} · ${line.label}`,
    hint: line.label,
  }))
);

export const getDeviceProductLine = (device: string): string | null => {
  for (const line of PRODUCT_LINES) {
    if (PRODUCT_LINE_TO_DEVICES[line.key as ProductLineKey].includes(device)) {
      return line.key;
    }
  }
  return null;
};

export const getDeviceDisplayLabel = (device: string): string => {
  const line = getDeviceProductLine(device);
  if (!line) return device;
  const lineLabel = PRODUCT_LINES.find((l) => l.key === line)?.label ?? line;
  return `${device} · ${lineLabel}`;
};

export const FILTERS: Record<string, FilterDefinition> = {
  agency: {
    key: 'agency',
    label: 'Regulatory Source',
    helperText: 'Filter by the upstream regulatory data feed.',
    group: 'authority',
    type: 'select',
    placeholder: 'All Regulatory Sources',
    clearValue: '',
    chipLabel: 'Source',
    chipClass: 'bg-blue-50 text-blue-900 border-blue-200',
    order: 10,
    options: [
      { value: 'FDA', label: 'FDA Enforcement' },
      { value: 'FDA_RECALLS', label: 'FDA Recalls' },
      { value: 'FDA_MAUDE', label: 'FDA MAUDE Adverse Events' },
    ],
  },
  urgency: {
    key: 'urgency',
    label: 'Priority Tier',
    helperText: 'Critical and High require same-day review.',
    group: 'severity',
    type: 'select',
    placeholder: 'All Priority Tiers',
    clearValue: '',
    chipLabel: 'Priority',
    chipClass: 'bg-rose-50 text-rose-900 border-rose-200',
    order: 20,
    options: [
      { value: 'critical', label: 'Critical Priority' },
      { value: 'high', label: 'High Priority' },
      { value: 'medium', label: 'Medium Priority' },
      { value: 'low', label: 'Low Priority' },
      { value: 'unclassified', label: 'Unclassified' },
    ],
  },
  type: {
    key: 'type',
    label: 'Signal Type',
    helperText: 'Filter by the regulatory event classification.',
    group: 'severity',
    type: 'select',
    placeholder: 'All Signal Types',
    clearValue: '',
    chipLabel: 'Type',
    chipClass: 'bg-violet-50 text-violet-900 border-violet-200',
    order: 25,
    options: [
      { value: 'recall', label: 'Recall' },
      { value: 'warning_letter', label: 'Warning Letter' },
      { value: 'fsca', label: 'FSCA' },
      { value: 'advisory', label: 'Advisory' },
      { value: 'guidance', label: 'Guidance' },
      { value: 'approval', label: 'Approval' },
      { value: 'market_update', label: 'Market Update' },
      { value: 'other', label: 'Other' },
    ],
  },
  market: {
    key: 'market',
    label: 'Market',
    helperText: 'Filter by the affected regulatory market.',
    group: 'authority',
    type: 'select',
    placeholder: 'All Markets',
    clearValue: '',
    chipLabel: 'Market',
    chipClass: 'bg-cyan-50 text-cyan-900 border-cyan-200',
    order: 15,
    options: [
      { value: 'US', label: 'United States' },
      { value: 'EU', label: 'European Union' },
      { value: 'UK', label: 'United Kingdom' },
      { value: 'CA', label: 'Canada' },
      { value: 'Global', label: 'Global' },
    ],
  },
  triage_status: {
    key: 'triage_status',
    label: 'Triage Status',
    helperText: 'Current disposition of the signal in your workflow.',
    group: 'severity',
    type: 'select',
    placeholder: 'All Triage Statuses',
    clearValue: '',
    chipLabel: 'Status',
    chipClass: 'bg-amber-50 text-amber-900 border-amber-200',
    order: 30,
    options: [
      { value: 'requires_action', label: 'Requires Action' },
      { value: 'monitoring', label: 'Monitoring' },
      { value: 'closed', label: 'Closed' },
    ],
  },
  productLine: {
    key: 'productLine',
    label: 'Product Line',
    helperText: 'Spacelabs brand or portfolio bucket.',
    group: 'product',
    type: 'select',
    placeholder: 'All Product Lines',
    clearValue: '',
    chipLabel: 'Product Line',
    chipClass: 'bg-blue-50 text-blue-900 border-blue-200',
    order: 40,
    options: PRODUCT_LINE_OPTIONS,
  },
  product: {
    key: 'product',
    label: 'Device Model',
    helperText: 'Filter by a specific device. Auto-selects its product line.',
    group: 'product',
    type: 'select',
    placeholder: 'All Devices',
    clearValue: '',
    chipLabel: 'Device',
    chipClass: 'bg-indigo-50 text-indigo-900 border-indigo-200',
    order: 50,
    options: (ctx: FilterContext) => {
      const lineKey = ctx.values.productLine ?? '';
      if (lineKey && PRODUCT_LINE_TO_DEVICES[lineKey as ProductLineKey]) {
        return DEVICE_OPTIONS_FOR_LINE(lineKey);
      }
      return ALL_DEVICE_OPTIONS;
    },
  },
  dateRange: {
    key: 'dateRange',
    label: 'Time Range',
    helperText: 'Limit results to signals published within the window.',
    group: 'time',
    type: 'select',
    placeholder: 'All Dates',
    clearValue: '',
    chipLabel: 'Date',
    chipClass: 'bg-emerald-50 text-emerald-900 border-emerald-200',
    order: 60,
    options: [
      { value: '7d', label: 'Last 7 Days' },
      { value: '30d', label: 'Last 30 Days' },
      { value: '90d', label: 'Last 90 Days' },
      { value: '1y', label: 'Past Year' },
    ],
  },
};

export const FILTER_KEYS = Object.keys(FILTERS);

export const TOOLBAR_FILTER_KEYS: string[] = Object.values(FILTERS)
  .sort((a, b) => a.order - b.order)
  .map((f) => f.key);

export const getFilterDisplayValue = (
  filterKey: string,
  rawValue: string,
  ctx: FilterContext = { values: {} }
): string => {
  if (!rawValue) return '';
  const def = FILTERS[filterKey];
  if (!def) return rawValue;
  const resolved = resolveOptions(def, ctx);
  const match = resolved.find((opt) => opt.value === rawValue);
  return match ? match.label : rawValue;
};

const resolveOptions = (def: FilterDefinition, ctx: FilterContext): FilterOption[] => {
  if (typeof def.options === 'function') {
    return def.options(ctx);
  }
  return def.options;
};

export interface ActiveFilterChip {
  key: string;
  label: string;
  value: string;
  displayValue: string;
  chipClass: string;
  group: FilterGroupId;
}

export const buildActiveFilterChips = (values: Record<string, string>): ActiveFilterChip[] => {
  const chips: ActiveFilterChip[] = [];
  for (const key of FILTER_KEYS) {
    const value = values[key];
    if (!value) continue;
    const def = FILTERS[key];
    chips.push({
      key,
      label: def.chipLabel,
      value,
      displayValue: getFilterDisplayValue(key, value, { values }),
      chipClass: def.chipClass,
      group: def.group,
    });
  }
  return chips;
};

export const getFiltersByGroup = (group: FilterGroupId): FilterDefinition[] =>
  FILTER_KEYS.map((k) => FILTERS[k]).filter((f) => f.group === group);
